import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Rating,
  CardMedia,
  CircularProgress,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  MenuBook as ReadIcon,
  Book as BookIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Favorite as FavoriteIcon,
  Category as CategoryIcon,
  VolumeUp as VolumeUpIcon,
  Star as StarIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { translations } from '../translations';
import * as PDFJS from "pdfjs-dist";
import { IBookDetails, Review } from '../types';
import PDFViewer from './PDFViewer';
import BookListener from './BookListener';
import { STORAGE_URL } from '../constants';
import { red } from '@mui/material/colors';

// Configure PDF.js worker
PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/4.2.67/pdf.worker.min.mjs`;

const BookDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [book, setBook] = useState<IBookDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [direction, setDirection] = useState(document.documentElement.dir);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [pdfExists, setPdfExists] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [showBookListener, setShowBookListener] = useState(false);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [hasRated, setHasRated] = useState(false);
  const [newReview, setNewReview] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [tempRating, setTempRating] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [hasLiked, setHasLiked] = useState(false);
  const [user, setUser] = useState<any>(null);

  const t = translations[direction === 'rtl' ? 'ku' : 'en'];

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'dir') {
          setDirection(document.documentElement.dir);
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchBookDetails = async () => {
      try {
        const response = await axios.get(`/api/books?id=${id}`);
        setBook({ ...response.data.book, related_books: response.data.related_books });
        setError(false);
      } catch (error) {
        console.error('Error fetching book details:', error);
        setError(true);
        setBook(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBookDetails();
  }, [id]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`/api/reviews?id=${id}`);
        setReviews(response.data.reviews || []);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews([]);
      }
    };

    if (id) {
      fetchReviews();
    }
  }, [id]);

  useEffect(() => {
    const checkPdfExists = async () => {
      if (book?.file_url.startsWith('http')) {
        setPdfExists(true);
        return;
      }
      if (book?.file_url) {
        try {
          const response = await fetch(`${STORAGE_URL}/${book.file_url}`);
          setPdfExists(response.ok);
        } catch (error) {
          setPdfExists(false);
        }
      }
    };

    checkPdfExists();
  }, [book?.file_url]);

  useEffect(() => {
    if (!book?.file_url) {
      setPdfUrl('');
      return;
    }

    const url = book.file_url.startsWith('http') 
      ? book.file_url 
      : pdfExists 
        ? `${STORAGE_URL}/${book.file_url}` 
        : '';
    
    setPdfUrl(url);
  }, [book?.file_url, pdfExists]);

  useEffect(() => {
    const storedRating = localStorage.getItem(`book-rating-${book?.id}`);
    if (storedRating) {
      setUserRating(Number(storedRating));
      setHasRated(true);
    }
  }, [book?.id]);

  useEffect(() => {
    const likedKey = `book-liked-${book?.id}`;
    const hasLiked = localStorage.getItem(likedKey);
    setHasLiked(!!hasLiked);
  }, [book?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleRatingClick = () => {
    if (hasRated) {
      setSnackbarMessage(t.alreadyRated);
      setSnackbarOpen(true);
      return;
    }
    setRatingDialogOpen(true);
  };

  const handleRatingSubmit = async () => {
    if (!tempRating) return;

    try {
      await axios.post('/api/ratings', {
        book_id: book?.id,
        rating: tempRating
      });
      
      localStorage.setItem(`book-rating-${book?.id}`, tempRating.toString());
      setUserRating(tempRating);
      setHasRated(true);
      setRatingDialogOpen(false);
      setSnackbarMessage(t.ratingSubmitted);
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error submitting rating:', error);
      setSnackbarMessage(t.reviewSubmissionError);
      setSnackbarOpen(true);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setReviewError(t.loginRequired || 'Please login to submit a review');
      return;
    }

    if (!newReview.trim()) {
      setReviewError(t.reviewRequired);
      return;
    }

    try {
      await axios.post('/api/reviews', {
        book_id: id,
        user_id: user.id,
        review: newReview,
        rating: userRating || 0
      });
      
      const response = await axios.get(`/api/reviews?id=${id}`);
      setReviews(response.data.reviews || []);
      setNewReview('');
      setReviewSuccess(true);
      setReviewError('');
      setTimeout(() => setReviewSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting review:', error);
      setReviewError(t.reviewSubmissionError);
    }
  };

  const handlePdfView = async () => {
    // Check if this book has been marked as downloaded
    const downloadedKey = `book-downloaded-${book?.id}`;
    const hasDownloaded = localStorage.getItem(downloadedKey);

    if (!hasDownloaded && book?.id) {
      try {
        await axios.post('/api/books', { id: book.id, type: 'downloaded' });
        localStorage.setItem(downloadedKey, 'true');
      } catch (error) {
        console.error('Error recording download:', error);
      }
    }

    setShowPdfViewer(!showPdfViewer);
  };

  const handleLikeClick = async () => {
    if (!book?.id || hasLiked) return;

    try {
      await axios.post('/api/books', { id: book.id, type: 'liked' });
      localStorage.setItem(`book-liked-${book.id}`, 'true');
      setHasLiked(true);
      // Update the book's like count locally
      setBook(prev => prev ? { ...prev, likes: (prev.likes || 0) + 1 } : null);
    } catch (error) {
      console.error('Error liking book:', error);
      // setSnackbarMessage(t.errorOccurred);
      // setSnackbarOpen(true);
    }
  };

  const handleRelatedBookClick = () => {
    window.scrollTo(0, 0);
  };

  const renderPdfViewer = () => {
    if (!pdfUrl || !pdfExists) {
      return (
        <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
          <Typography variant="body1" align="center" color="text.secondary">
            {t.pdfNotAvailable}
          </Typography>
        </Paper>
      );
    }
    return (
      <>
        <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePdfView}
              startIcon={<ReadIcon />}
              sx={{ '& .MuiButton-startIcon': { mr: 1 } }}
            >
              {showPdfViewer ? t.close : t.readPdf}
            </Button>
          </Box>
          {showPdfViewer && <PDFViewer pdfUrl={pdfUrl} />}
        </Paper>
      </>
    );
  };

  const renderBookListener = () => {
    if (!book?.audio_file_url) {
      return (
        <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
          <Typography variant="body1" align="center" color="text.secondary">
            {t.bookAudioFileNotAvailable}
          </Typography>
        </Paper>
      ); 
    }

    if (book.is_paid) {
      return (
        <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2 }}>
            {t.paidBookAudioMessage || 'This audiobook is only available on our mobile app'}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2 
          }}>
            <IconButton
              href="https://play.google.com/store/apps/details?id=net.kurdcode.ktebstan"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <AndroidIcon sx={{ fontSize: 32 }} />
            </IconButton>
            <IconButton
              href="https://apps.apple.com/us/app/ktebstan-کتێبستان/id1457469282"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <AppleIcon sx={{ fontSize: 32 }} />
            </IconButton>
          </Box>
        </Paper>
      );
    }

    return (
      <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowBookListener(!showBookListener)}
            startIcon={<VolumeUpIcon />}
            sx={{ '& .MuiButton-startIcon': { mr: 1 } }}
          >
            {showBookListener ? t.close : t.listenBook}
          </Button>
        </Box>
        {showBookListener && (
          <BookListener
            audioUrl={book.audio_file_url}
            maxChapters={book?.audio_chapters || 1}
            onClose={() => setShowBookListener(false)}
          />
        )}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !book) {
    return (
      <Container>
        <Typography variant="h5" color="error" sx={{ mt: 4, textAlign: 'center' }}>
          {t.bookNotFound}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        {/* Book Cover Column */}
        <Grid item xs={12} md={4} sx={{ 
          order: { md: direction === 'rtl' ? 2 : 1 }
        }}>
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Box sx={{ 
              position: 'relative', 
              pb: 2,
              display: 'flex',
              justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start'
            }}>
              {book.cover_image_url ? (
                <CardMedia
                  component="img"
                  image={book.cover_image_url.startsWith('http') ? book.cover_image_url : `/images/${book.cover_image_url}`}
                  alt={book.title}
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    maxHeight: 500,
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/ktebstan.jpeg';
                  }}
                />
              ) : (
                <Box
                  className="fallback-icon"
                  sx={{
                    width: '100%',
                    height: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                  }}
                >
                  <BookIcon sx={{ fontSize: 100, color: 'grey.400' }} />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Book Details Column */}
        <Grid item xs={12} md={8} sx={{ 
          order: { md: direction === 'rtl' ? 1 : 2 }
        }}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h4" gutterBottom>
              {book.title}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              {book.author && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" color="text.secondary">
                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t.author}: <Link
                      to={`/authors/${book.author.id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      {direction === 'rtl' ? book.author.name_ku : book.author.name}
                    </Link>
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" color="text.secondary">
                  <Rating
                    icon={<StarIcon 
                      sx={{ 
                        mr: 1, 
                        verticalAlign: 'middle',
                        animation: !hasRated ? 'starPulse 2s infinite' : 'none',
                        transition: 'all 0.3s ease',
                        transform: 'scale(1)',
                        '&:hover': {
                          transform: !hasRated ? 'scale(1.2)' : 'scale(1)',
                        },
                        '@keyframes starPulse': {
                          '0%': {
                            transform: 'scale(1) rotate(0deg)',
                          },
                          '50%': {
                            transform: 'scale(1.1) rotate(10deg)',
                          },
                          '100%': {
                            transform: 'scale(1) rotate(0deg)',
                          },
                        }
                      }} 
                    />}
                    value={userRating || book.average_rating || 0}
                    precision={1}
                    readOnly={hasRated}
                    onClick={handleRatingClick}
                    sx={{ 
                      mr: 1, 
                      cursor: hasRated ? 'default' : 'pointer',
                      '& .MuiRating-icon': {
                        transition: 'transform 0.3s ease',
                      },
                      '& .MuiRating-iconEmpty:hover': !hasRated ? {
                        transform: 'scale(1.2)',
                      } : {}
                    }}
                  />
                  <Typography variant="subtitle1" color="text.secondary">
                    ({book.rating_count || 0} {t.ratings})
                  </Typography>
                </Typography>
                {!hasRated && (
                  <Typography 
                    variant="caption" 
                    color="primary" 
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      animation: 'fadeInOut 2s infinite',
                      '@keyframes fadeInOut': {
                        '0%': { opacity: 0.6 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.6 },
                      }
                    }}
                  >
                    {t.clickToRate}
                  </Typography>
                )}
              </Grid>

              {book.category && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" color="text.secondary">
                    <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t.category}: {book.category}
                  </Typography>
                </Grid>
              )}

              {book.page_count && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" color="text.secondary">
                    <ReadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t.pages}: {book.page_count}
                  </Typography>
                </Grid>
              )}

              {book.publication_year && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" color="text.secondary">
                    <BookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {t.publicationYear}: {book.publication_year}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {book.description && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {t.description}
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2,
                    bgcolor: 'grey.50',
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}
                >
                  <Typography variant="body1">
                    {book.description}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Stats Box */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              justifyContent: 'center',
              mt: 3,
              borderRadius: 1,
              bgcolor: 'grey.50',
              p: 2
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <VisibilityIcon color="action" />
                <Typography variant="h6">{book.viewed || 0}</Typography>
                <Typography variant="body2" color="text.secondary">{t.views}</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <DownloadIcon color="action" />
                <Typography variant="h6">{book.download || 0}</Typography>
                <Typography variant="body2" color="text.secondary">{t.downloads}</Typography>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                cursor: hasLiked ? 'default' : 'pointer',
                '&:hover': !hasLiked ? {
                  '& .like-icon': {
                    transform: 'scale(1.2)',
                  }
                } : {}
              }}
              onClick={handleLikeClick}
              >
                <FavoriteIcon 
                  className="like-icon"
                  color="action" 
                  sx={{ 
                    color: hasLiked ? red[500] : 'action.active',
                    transition: 'all 0.3s ease',
                    animation: !hasLiked ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                      },
                      '100%': {
                        transform: 'scale(1)',
                      },
                    }
                  }} 
                />
                <Typography variant="h6">{book.likes || 0}</Typography>
                <Typography variant="body2" color="text.secondary">{t.likes}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {renderPdfViewer()}
      {renderBookListener()}

      {/* Reviews Section */}
      <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t.reviews} ({reviews.length})
        </Typography>

        {/* New Review Form */}
        <Box component="form" onSubmit={handleSubmitReview} sx={{ mb: 4 }}>
          {user ? (
            <>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={newReview}
                onChange={(e) => setNewReview(e.target.value)}
                label={t.writeReview}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              {reviewError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {reviewError}
                </Alert>
              )}
              {reviewSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t.reviewSubmitted}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
              >
                {t.submitReview}
              </Button>
            </>
          ) : (
            <Alert severity="info">
              {t.loginRequired}
            </Alert>
          )}
        </Box>

        {/* Reviews List */}
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <Box
              key={review.id}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                py: 2,
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Link 
                  to={`/users/${review.user_id}`}
                  style={{ 
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {review.profile_image_url ? (
                    <Box
                      component="img"
                      src={review.profile_image_url}
                      alt={review.user_name}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        mr: 1,
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/images/default-avatar.png';
                      }}
                    />
                  ) : (
                    <PersonIcon sx={{ mr: 1 }} />
                  )}
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      '&:hover': { 
                        textDecoration: 'underline' 
                      } 
                    }}
                  >
                    {review.user_name}
                  </Typography>
                </Link>
                <Rating
                  value={review.rating}
                  readOnly
                  size="small"
                  sx={{ ml: 2 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {new Date(review.created_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Typography variant="body1">{review.review}</Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {t.noReviews}
          </Typography>
        )}
      </Paper>

      {/* Related Books Section */}
      <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t.relatedBooks}
        </Typography>
        
        {book.related_books && book.related_books.length > 0 ? (
          <Grid container spacing={3}>
            {book.related_books.map((relatedBook) => (
              <Grid item xs={12} sm={6} md={3} key={relatedBook.id}>
                <Paper 
                  elevation={2}
                  sx={{ 
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    }
                  }}
                >
                  <Link 
                    to={`/books/${relatedBook.id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                    onClick={handleRelatedBookClick}
                  >
                    {relatedBook.cover_image_url ? (
                      <CardMedia
                        component="img"
                        image={relatedBook.cover_image_url.startsWith('http') ? relatedBook.cover_image_url : `/images/${relatedBook.cover_image_url}`}
                        alt={relatedBook.title}
                        sx={{
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 2
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/images/ktebstan.jpeg';
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                          borderRadius: 1,
                          mb: 2
                        }}
                      >
                        <BookIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                      </Box>
                    )}
                    
                    <Typography variant="subtitle1" gutterBottom>
                      {relatedBook.title}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {relatedBook.authors?.join(', ')}
                    </Typography>
                    
                    <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center' }}>
                      <Rating
                        value={relatedBook.average_rating || 0}
                        precision={0.5}
                        readOnly
                        size="small"
                      />
                    </Box>
                  </Link>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            {t.noRelatedBooks}
          </Typography>
        )}
      </Paper>

      <Dialog open={ratingDialogOpen} onClose={() => setRatingDialogOpen(false)}>
        <DialogTitle>{t.rateThisBook}</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Rating
              value={tempRating}
              precision={1}
              onChange={(event, newValue) => setTempRating(newValue)}
              size="large"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingDialogOpen(false)}>{t.cancel}</Button>
          <Button onClick={handleRatingSubmit} disabled={!tempRating}>
            {t.submitReview}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default BookDetails; 